import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import StructuredData from "@/components/StructuredData";
import SEOHead from "@/components/SEOHead";
import { Phone, Mail, MapPin, Clock, Brush, Send, MessageCircle, Home, Image, User, ArrowRight, Heart, Palette, Award, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { sendEmail, sendVKMessage, type ContactFormData } from "@/lib/email";
import { trackSocialClick } from "@/lib/analytics";
import { trackEvent } from "@/config/integrations";

const Contacts = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const emailData: ContactFormData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
    };

    try {
      // Отправляем email и уведомление в ВК параллельно
      const [emailSent, vkSent] = await Promise.all([
        sendEmail(emailData),
        sendVKMessage(emailData)
      ]);

      if (emailSent || vkSent) {
        // Трекинг успешной отправки формы
        trackEvent('contact_form_submit', {
          form_name: 'contact_form',
          success: true,
          timestamp: Date.now()
        });
        
        toast({
          title: "Сообщение отправлено!",
          description: "Спасибо за ваше обращение. Мы свяжемся с вами в ближайшее время.",
        });
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      console.error('Email sending error:', error);
      
      // Специальная обработка rate limit ошибки
      if (error.message.startsWith('RATE_LIMIT_EXCEEDED')) {
        const waitMinutes = error.message.split(':')[1] || '2';
        toast({
          title: "Превышен лимит запросов", 
          description: `Слишком много сообщений. Повторите через ${waitMinutes} мин.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Ошибка отправки",
          description: "Произошла ошибка при отправке сообщения. Попробуйте еще раз или свяжитесь с нами напрямую.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "Телефоны",
      details: [
        { label: "Основной", value: "8-908-800-57-99", action: "tel:+79088005799" },
        { label: "Дополнительный", value: "8-950-781-87-85", action: "tel:+79507818785" }
      ]
    },
    {
      icon: Mail,
      title: "Email",
      details: [
        { label: "Для заказов", value: "info@w-wagner.art", action: "mailto:info@w-wagner.art" }
      ]
    },
    {
      icon: MapPin,
      title: "Мастерская",
      details: [
        { label: "Адрес", value: "г. Омск, ул. 21 Амурская 37" },
        { label: "Район", value: "Амур 2" }
      ]
    }
  ];

  const socialLinks = [
    { 
      name: "VK Сообщения", 
      url: "https://vk.com/im?sel=-214021218", 
      color: "text-blue-500",
      description: "Быстрая связь",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zM12 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm6-10.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5z"/>
          <circle cx="12" cy="12" r="3" fill="white"/>
          <text x="12" y="8" textAnchor="middle" fontSize="4" fill="currentColor">VK</text>
        </svg>
      )
    },
    { 
      name: "VK Сообщество", 
      url: "https://vk.com/w_wagner_art", 
      color: "text-blue-600",
      description: "Галерея работ",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0z"/>
        </svg>
      )
    },
    { 
      name: "WhatsApp", 
      url: "https://wa.me/79507818785", 
      color: "text-green-500",
      description: "Мгновенная связь",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      )
    },
    { 
      name: "Telegram", 
      url: "https://t.me/FallenSonS",
      color: "text-blue-400",
      description: "Быстрый чат",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      )
    },
  ];

  const workingHours = [
    { day: "Понедельник - Пятница", hours: "9:00 - 21:00" },
    { day: "Суббота - Воскресенье", hours: "10:00 - 21:00" }
  ];

  const services = [
    { icon: Brush, title: "Картины на заказ", desc: "Пейзажи, абстракции" },
    { icon: MessageCircle, title: "Роспись стен", desc: "Кафе, офисы, детские комнаты" },
    { icon: Brush, title: "Роспись стен", desc: "Интерьерная и экстерьерная" }
  ];

  return (
    <>
      <SEOHead
        title="Контакты художника Валерии Вагнер в Омске | Заказать картину | W-Wagner.art"
        description="Свяжитесь с художником Валерией Вагнер в Омске. Заказать картину маслом или акрилом, роспись стен. Мастерская: ул. 21 Амурская 37, Амур 2. Телефон: 8-908-800-57-99, 8-950-781-87-85. Email: info@w-wagner.art"
        keywords={['контакты художника Омск', 'заказать картину Омск', 'художник Омск телефон', 'мастерская художника Омск', 'адрес художника Омск', 'Валерия Вагнер контакты']}
        canonicalUrl="https://w-wagner.art/contacts"
      />
      <StructuredData type="contact" />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-card via-background to-card/50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.1),transparent_50%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="text-center space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <Palette className="w-4 h-4" />
                  Художник из Омска
                </div>
                <h1 className="text-6xl md:text-8xl font-bold">
                  <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                    Контакты
                  </span>
                </h1>
                <p className="text-2xl md:text-3xl text-muted-foreground font-light max-w-4xl mx-auto">
                  Готова воплотить ваши идеи в уникальные произведения искусства. 
                  Работаю по всему Омску и Омской области.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3">
                {["Живопись маслом", "Роспись стен", "Акриловая живопись", "Пейзажи Омска"].map((tag) => (
                  <Badge 
                    key={tag}
                    variant="outline" 
                    className="text-base px-6 py-3 border-primary/30 hover:border-primary/50 transition-colors"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto">
                Давайте 
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> создадим</span>
                {" "}что-то особенное
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
                Обсудим ваши идеи и воплотим их в уникальное произведение искусства в Омске
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div className="space-y-8">
                <Card className="border-2 border-primary/30 shadow-2xl bg-gradient-to-br from-card via-background/95 to-card/80 backdrop-blur-sm ring-1 ring-primary/10">
                  <CardHeader className="pb-8 bg-gradient-to-r from-primary/5 to-accent/5 rounded-t-lg">
                    <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-3xl text-center font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Написать мне
                    </CardTitle>
                    <p className="text-muted-foreground text-lg text-center">
                      Расскажите о своей идее и получите персональное предложение
                    </p>
                  </CardHeader>
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <Label htmlFor="name" className="text-base font-medium">Имя *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Как к вам обращаться?"
                            className="h-12 border-border/30 focus:border-primary/50 bg-background/50"
                          />
                        </div>
                        <div className="space-y-3">
                          <Label htmlFor="phone" className="text-base font-medium">Телефон</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+7 (999) 123-45-67"
                            className="h-12 border-border/30 focus:border-primary/50 bg-background/50"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label htmlFor="email" className="text-base font-medium">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="your@email.com"
                          className="h-12 border-border/30 focus:border-primary/50 bg-background/50"
                        />
                      </div>

                      <div className="space-y-3">
                        <Label htmlFor="message" className="text-base font-medium">Расскажите о проекте</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Какую картину хотите заказать? Какие размеры? Стиль? Сроки? Любые детали помогут мне лучше понять ваши пожелания..."
                          className="min-h-[140px] border-border/30 focus:border-primary/50 resize-none bg-background/50"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full gold-gradient text-black font-semibold h-14 text-lg rounded-xl"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                            Отправляем...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Отправить сообщение
                          </div>
                        )}
                      </Button>

                      <p className="text-center text-sm text-muted-foreground">
                        Обычно отвечаю в течение нескольких часов
                      </p>
                    </form>
                  </CardContent>
                </Card>

                {/* Quick Contact Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Card className="border-0 bg-gradient-to-br from-primary/10 to-accent/5 hover:from-primary/15 hover:to-accent/10 transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <Phone className="w-8 h-8 text-primary mx-auto mb-3" />
                      <h3 className="font-semibold mb-2">Позвонить сейчас</h3>
                      <a href="tel:+79088005799" className="text-lg font-medium text-primary hover:text-primary/80 transition-colors">
                        8-908-800-57-99
                      </a>
                      <p className="text-sm text-muted-foreground mt-1">Основной номер</p>
                    </CardContent>
                  </Card>

                  <Card className="border-0 bg-gradient-to-br from-accent/10 to-primary/5 hover:from-accent/15 hover:to-primary/10 transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <MessageCircle className="w-8 h-8 text-accent mx-auto mb-3" />
                      <h3 className="font-semibold mb-2">Написать в WhatsApp</h3>
                        <a 
                         href="https://wa.me/79507818785?" 
                         target="_blank"
                         rel="noopener noreferrer"
                          onClick={() => trackSocialClick('WhatsApp', 'страница контактов')}
                         className="text-lg font-medium text-accent hover:text-accent/80 transition-colors"
                      >
                        Открыть чат
                      </a>
                      <p className="text-sm text-muted-foreground mt-1">Быстрый ответ</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                {/* Main Contact Card */}
                <Card className="border-0 shadow-xl bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-sm">
                  <CardContent className="p-8">
                    <div className="space-y-8">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                          <MapPin className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Мастерская в Омске</h3>
                        <p className="text-lg text-muted-foreground">
                          ул. 21 Амурская 37, район Амур 2
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="text-center p-4 rounded-xl bg-background/50">
                          <Phone className="w-6 h-6 text-primary mx-auto mb-2" />
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Основной</p>
                            <a href="tel:+79088005799" className="block font-semibold text-primary">
                              8-908-800-57-99
                            </a>
                          </div>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-background/50">
                          <Phone className="w-6 h-6 text-accent mx-auto mb-2" />
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Дополнительный</p>
                            <a href="tel:+79507818785" className="block font-semibold text-accent">
                              8-950-781-87-85
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="text-center p-4 rounded-xl bg-background/50">
                        <Mail className="w-6 h-6 text-primary mx-auto mb-2" />
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Электронная почта</p>
                          <a href="mailto:info@w-wagner.art" className="block font-semibold text-primary">
                            info@w-wagner.art
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>


                {/* Creative Process & Consultation */}
                <Card className="border-0 bg-gradient-to-br from-accent/10 via-card/60 to-primary/5 backdrop-blur-sm">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <Palette className="w-6 h-6 text-accent" />
                      Как мы работаем
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4 p-4 rounded-lg bg-background/30">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">1</span>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Консультация</h4>
                          <p className="text-sm text-muted-foreground">Обсуждаем ваши идеи, стиль, размеры и сроки</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4 p-4 rounded-lg bg-background/30">
                        <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-accent font-bold text-sm">2</span>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Эскиз</h4>
                          <p className="text-sm text-muted-foreground">Создаю предварительный набросок для утверждения</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4 p-4 rounded-lg bg-background/30">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">3</span>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Создание</h4>
                          <p className="text-sm text-muted-foreground">Воплощаю идею в жизнь с регулярными обновлениями</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-border/30">
                      <div className="flex items-center gap-2 text-accent mb-3">
                        <Heart className="w-4 h-4" />
                        <span className="font-medium">Индивидуальный подход</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Каждый проект уникален. Учитываю все ваши пожелания по стилю, цветовой гамме и технике исполнения. 
                        Работаю с различными материалами и размерами — от миниатюр до масштабных композиций.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Working Hours Section */}
        <section className="py-20 bg-gradient-to-br from-card/40 via-background to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium mb-6">
                <Clock className="w-4 h-4" />
                Мастерская открыта
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto">
                Режим 
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> мастерской</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                Планируйте свой визит в творческое пространство художника в самом сердце Омска. 
                Предварительная запись обязательна для комфортного общения и консультации.
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Enhanced Schedule */}
                <div className="lg:col-span-2">
                  <Card className="border-0 shadow-xl bg-card/80 backdrop-blur-sm">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-semibold mb-8 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-primary" />
                        </div>
                        Расписание работы
                      </h3>
                      <div className="space-y-4">
                        {workingHours.map((schedule, index) => (
                          <div key={index} className="flex justify-between items-center py-4 px-6 rounded-xl bg-gradient-to-r from-background/60 to-background/30 border border-border/30 hover:border-primary/20 transition-all">
                            <span className="text-muted-foreground font-medium text-lg">{schedule.day}</span>
                            <span className="font-bold text-primary text-lg">{schedule.hours}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-8 p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-primary font-bold text-sm">!</span>
                          </div>
                          <div>
                            <p className="font-semibold text-primary mb-2">Важная информация</p>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              Предварительная запись обязательна. Это поможет мне лучше подготовиться к встрече 
                              и уделить вам максимум внимания при обсуждении вашего проекта.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Enhanced Location & Quick Actions */}
                <div className="space-y-6">
                  <Card className="border-0 shadow-xl bg-gradient-to-br from-accent/10 to-primary/5 backdrop-blur-sm">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-accent" />
                        Адрес мастерской
                      </h3>
                      <div className="space-y-4">
                        <div className="p-4 bg-background/50 rounded-lg">
                          <p className="font-semibold text-lg">ул. 21 Амурская 37</p>
                          <p className="text-sm text-muted-foreground">район Амур 2, Омск</p>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Brush className="w-4 h-4 text-primary" />
                            Что здесь происходит
                          </h4>
                          <div className="space-y-2">
                            {services.slice(0, 3).map((service, index) => (
                              <div key={index} className="flex items-center gap-2 text-sm p-2 rounded bg-background/30">
                                <service.icon className="w-4 h-4 text-primary flex-shrink-0" />
                                <span>{service.title}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 bg-gradient-to-br from-primary/10 to-accent/5">
                    <CardContent className="p-6 text-center">
                      <Phone className="w-8 h-8 text-primary mx-auto mb-3" />
                      <h4 className="font-semibold mb-2">Быстрая запись</h4>
                      <a href="tel:+79088005799" className="text-lg font-medium text-primary hover:text-primary/80 transition-colors block mb-2">
                        8-908-800-57-99
                      </a>
                      <p className="text-xs text-muted-foreground">Звоните в рабочие часы</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Enhanced to match Workshop Section width */}
        <section className="py-24 bg-gradient-to-br from-primary/5 via-transparent to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium mb-6">
                <Palette className="w-4 h-4" />
                Творческое сотрудничество
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto">
                Воплотим ваши идеи
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> в искусство</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                Каждая картина — это уникальная история. Расскажите мне о своих идеях, 
                и мы создадим произведение, которое будет радовать вас долгие годы. 
                Работаю по всему Омску и Омской области.
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 rounded-3xl" />
                <div className="relative p-12 space-y-8 text-center">

                  <div className="flex flex-col sm:flex-row gap-6 justify-center">
                    <Button asChild size="lg" className="gold-gradient text-black font-semibold text-lg px-8 py-4 h-auto">
                      <a href="tel:+79088005799">
                        <Phone className="w-5 h-5 mr-2" />
                        Позвонить: 8-908-800-57-99
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="text-lg px-8 py-4 h-auto border-primary/30 hover:border-primary/50">
                       <a href="https://wa.me/79507818785?" target="_blank" rel="noopener noreferrer" onClick={() => trackSocialClick('WhatsApp', 'страница контактов')}>
                         <MessageCircle className="w-5 h-5 mr-2" />
                         WhatsApp
                       </a>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="text-lg px-8 py-4 h-auto border-accent/30 hover:border-accent/50">
                      <Link to="/raboty">
                        <Image className="w-5 h-5 mr-2" />
                        Портфолио
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contacts;